<template>
  <div class="form-element" :class="bgStyle">
    <FormLabel
      v-if="label"
      v-bind="{
        label,
        disabled,
        description,
      }"
    />
    <div class="row">
      <div class="col-3 pl-0">
        <Multiselect
          :disabled="disabled"
          :options="filterConditions"
          :searchable="true"
          placeholder="Select condition"
          :value="filter.condition"
          :custom-label="decamelize"
          @input="updateFilterField('condition', $event)"
        />
      </div>
      <div v-if="shouldShowFields" class="col-3 pl-0">
        <Multiselect
          :disabled="disabled"
          :options="fieldSelectOptions"
          :searchable="true"
          placeholder="Select field"
          :custom-label="e => e.label"
          :value="filter"
          @input="updateFilterField('name', $event)"
        />
      </div>
      <div v-if="shouldShowFields" class="col-3 pl-0">
        <Multiselect
          :disabled="disabled"
          :options="options[filter.inputType] || options.text"
          :custom-label="decamelize"
          :searchable="true"
          placeholder="Select type"
          :value="filter.type"
          @input="updateFilterField('type', $event)"
        />
      </div>
      <div v-if="shouldShowFields" class="col-3 pl-0">
        <DaysSelect
          v-if="shouldShowWeekdaySelect"
          :disabled="disabled"
          class="p-0"
          :value="filter.value"
          @input="updateFilterField('value', $event)"
        />

        <FormDepartmentSelect
          v-else-if="shouldShowDepartmentSelect"
          class="p-0 mt-0"
          :value="normalizedValue"
          :disabled="disabled"
          :multiple="true"
          :config="{hideLabel: true, placeholder: 'Search'}"
          @input="updateFilterField('value', $event)"
        />
        <input
          v-else-if="shouldShowTextInput"
          class="form-control"
          placeholder="Enter Value"
          :type="filter.inputType || 'text'"
          :value="filter.value"
          :disabled="disabled"
          @input="updateFilterField('value', $event.target.value)"
        />

      </div>
    </div>

    <div v-if="shouldShowSubFilters">
      <div
        v-for="(subFilter, filterIndex) in subFilters"
        :key="subFilter.id + filterIndex"
        class="row"
      >
        <div class="col-11">
          <SectionsFilter
            :disabled="disabled"
            :value="subFilter"
            :depth="depth + 1"
            @input="updateSubFilter(filterIndex, $event)"
          />
        </div>
        <div class="col-1 pl-0">
          <button
            v-if="!disabled"
            class="btn btn-sm btn-danger"
            type="button"
            @click="deleteSubFilter(filterIndex)"
          >
            <i class="material-icons">remove_circle</i>
          </button>
        </div>
      </div>
      <button v-if="!disabled" class="btn btn-primary pl-2 mb-3" type="button" @click="addSubFilter">
        <i class="material-icons pr-2">add_circle</i> Add Condition
      </button>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { computed } from '@vue/composition-api'
import Multiselect from '@/components/Multiselect'
import DaysSelect from '@/components/DaysSelect'
import FormDepartmentSelect from '@/components/Forms/FormDepartmentSelect'
import FormLabel from '@/components/Forms/FormLabel'
import useSectionTemplate from '@sm/client/composables/useSectionTemplate'

export default {
  name: 'SectionsFilter',
  inheritAttrs: false,
  components: { Multiselect, DaysSelect, FormDepartmentSelect, FormLabel },
  props: {
    depth: { type: Number, default: 0 },
    disabled: { type: Boolean },
    value: { type: Object },
    label: {
      type: String,
      default: '',
    },
    description: {
      type: String,
      default: '',
    },
  },
  data () {
    return {
      bgStyle: this.depth % 2 === 0 ? 'filtereven' : 'filterodd',
      filterConditions: [],
      filterSpecificFields: {
        startTime: {
          name: 'startTime',
          label: 'Start Time',
          inputType: 'time',
        },
        endTime: {
          name: 'endTime',
          label: 'End Time',
          inputType: 'time',
        },
        instructorName: {
          name: 'instructorName',
          label: 'Instructor Name',
          inputType: 'text',
        },
        roomName: {
          name: 'roomName',
          label: 'Room Name',
          inputType: 'text',
        },
        roomNumber: {
          name: 'roomNumber',
          label: 'Room Number',
          inputType: 'number',
        },
        days: {
          name: 'days',
          label: 'Days',
          inputType: 'days',
        },
      },
      options: {
        text: [
          'contains',
          'doesNotContain',
          'is',
          'isNot',
          'isEmpty',
          'isNotEmpty',
          'startsWith',
          'endsWith',
          'longerThan',
          'shorterThan',
        ],
        number: [
          'contains',
          'doesNotContain',
          'is',
          'isNot',
          'isEmpty',
          'isNotEmpty',
          'startsWith',
          'endsWith',
          'longerThan',
          'shorterThan',
          'greaterThan',
          'lessThan',
        ],
        date: [
          'is',
          'isNot',
          'isAfter',
          'isBefore',
        ],
        time: [
          'is',
          'isNot',
          'isAfter',
          'isOnOrAfter',
          'isBefore',
          'isOnOrBefore',
        ],
        days: [
          'contains',
          'isContainedBy',
          'doesNotContain',
          'isNotContainedBy',
          'overlaps',
          'doesNotOverlap',
          'is',
          'isNot',
        ],
        select: [
          'contains',
          'doesNotContain',
          'is',
          'isNot',
          'isEmpty',
          'isNotEmpty',
          'startsWith',
          'endsWith',
          'longerThan',
          'shorterThan',
        ],
        departmentSelect: [
          'contains',
          'doesNotContain',
          'is',
          'isNot',
          'isEmpty',
          'isNotEmpty',
        ],
        multipleSelect: [
          'contains',
          'doesNotContain',
          'is',
          'isNot',
          'isEmpty',
          'isNotEmpty',
          'startsWith',
          'endsWith',
        ],
      },
      filter: {
        condition: 'allowAll',
        name: undefined,
        type: undefined,
        value: undefined,
        label: undefined,
        group: undefined,
        inputType: undefined,
        subFilters: undefined,
      },
    }
  },
  setup () {
    const { sectionTemplate } = useSectionTemplate()

    const sectionTemplateQuestions = computed(() =>
      sectionTemplate.value.flattenQuestionsObjWithId2to1()
    )

    return {
      sectionTemplateQuestions,
    }
  },
  computed: {
    ...mapGetters([ 'courseFieldOptions' ]),
    normalizedValue () {
      if (this.filter.inputType === 'departmentSelect') {
        if (typeof this.filter.value === 'string' && this.filter.value.includes(',')) return this.filter.value.split(',')
        else {
          const value = Array.isArray(this.filter.value) ? this.filter.value : [ this.filter.value ]
          return value.filter(Boolean)
        }
      } else return this.filter.value
    },
    fieldSelectOptions () {
      const sectionFields = Object.values({
        ...this.sectionTemplateQuestions,
        ...this.filterSpecificFields,
      }).map(question => ({
        name: question.name,
        group: 'section',
        label: question.label,
        inputType: question.inputType,
      }))
      const courseFields = this.courseFieldOptions.map(el => ({
        name: el.name,
        group: 'course',
        label: el.label,
        inputType: el.name === 'departments' ? 'departmentSelect' : el.inputType,
      }))
      return [ ...courseFields, ...sectionFields ]
    },
    shouldShowSubFilters () {
      return this.filter.condition === 'allOf' || this.filter.condition === 'anyOf'
    },
    shouldShowFields () {
      return this.filter.condition === 'field'
    },
    shouldShowWeekdaySelect () {
      return this.filter.inputType === 'days'
    },
    shouldShowDepartmentSelect () {
      return this.filter.inputType === 'departmentSelect'
    },
    shouldShowTextInput () {
      return this.filter.type !== 'isEmpty' && this.filter.type !== "isNotEmpty'"
    },
    subFilters () {
      return this.filter.subFilters || []
    },
  },
  created () {
    if (this.value) {
      this.filter = { ...this.value }
    } else if (this.filterData) {
      this.filter = { ...this.filterData }
    }
    if (!this.filter.id) this.filter.id = this.generateToken(8)

    if (this.depth === 0) this.filterConditions.push('allowAll')
    this.filterConditions.push('field')
    if (this.depth < 3) this.filterConditions.push('anyOf', 'allOf')
    if (!this.filter.condition) {
      this.updateFilterField('condition', this.filterConditions[0])
    }
  },
  methods: {
    update () {
      this.$emit('input', this.filter)
    },
    deleteSubFilter (filterIndex) {
      this.$delete(this.filter.subFilters, filterIndex)
      this.update()
    },
    addSubFilter () {
      this.filter.subFilters.push({
        id: this.generateToken(8),
      })
      this.update()
    },
    updateSubFilter (filterIndex, subFilter) {
      this.$set(this.filter.subFilters, filterIndex, subFilter)
      this.update()
    },
    updateFilterField (key, value) {
      if (key === 'condition') {
        this.updateFilterCondition(value)
      } else if (key === 'name') {
        this.updateFilterName(value)
      } else {
        this.filter[key] = value
      }
      this.update()
    },
    updateFilterName (value) {
      const inputType = value.inputType
      const filterTypes = this.options[inputType] || this.options.text
      const firstType = filterTypes[0] || 'contains'
      this.filter.name = value.name
      this.filter.type = firstType
      this.filter.group = value.group
      this.filter.value = inputType === 'days' ? [] : ''
      this.filter.inputType = inputType
      this.filter.label = value.label
    },
    updateFilterCondition (newCondition) {
      this.$set(this.filter, 'condition', newCondition)
      if (newCondition === 'allOf' || newCondition === 'anyOf') {
        if (!this.filter.subFilters) {
          this.$set(this.filter, 'subFilters', [
            this.defaultFilter(),
            this.defaultFilter(),
          ])
        }
      } else {
        this.$set(this.filter, 'subFilters', undefined)
      }
      if (newCondition === 'field') {
        this.filter = this.defaultFilter()
      } else {
        this.filter.name = undefined
        this.filter.type = undefined
        this.filter.value = undefined
        this.filter.inputType = undefined
        this.filter.group = undefined
        this.filter.label = undefined
      }
    },
    defaultFilter () {
      return {
        id: this.generateToken(8),
        condition: 'field',
        name: 'sectionType',
        label: 'Section Type',
        inputType: 'text',
        group: 'section',
        type: 'contains',
        value: '',
        subFilters: undefined,
      }
    },
  },
}
</script>

<style scoped>

[class*="col"] {
  padding-bottom: 0.8rem;
}

.filtereven {
  background: #fff;
  border-radius: 10px;
  padding: 5px;
}

.filterodd {
  background: #ccf;
  border-radius: 10px;
  padding: 5px;
}

</style>
